<script setup lang="ts">
import { computed } from 'vue';
import { useI18n } from 'vue-i18n';
import type { PendingUpload } from '@/composables/useSnapshot';

const { t } = useI18n();

const props = defineProps<{
  upload: PendingUpload;
}>();

const statusText = computed(() => {
  switch (props.upload.status) {
    case 'uploading':
      return t('chat.uploading');
    case 'error':
      return props.upload.errorMessage ?? t('chat.uploadFailed');
    default:
      return '';
  }
});
</script>

<template>
  <div class="uploading-message" :class="`uploading-message--${upload.status}`">
    <div class="uploading-message__content">
      <div class="uploading-message__header">
        <span class="uploading-message__status">
          <span v-if="upload.status === 'uploading'" class="uploading-message__spinner"></span>
          {{ statusText }}
        </span>
      </div>

      <div class="uploading-message__image-container">
        <img
          :src="upload.previewUrl"
          class="uploading-message__image"
          :alt="t('chat.imageAlt')"
        />
      </div>
    </div>
  </div>
</template>

<style scoped>
.uploading-message {
  display: flex;
  flex-direction: column;
  max-width: 80%;
  margin-bottom: var(--spacing-sm);
  padding: var(--spacing-xs) var(--spacing-sm);
  border-radius: var(--border-radius);
  border: 1px dashed var(--color-border);
  word-wrap: break-word;
  overflow-wrap: break-word;
  align-self: flex-end;
  background: linear-gradient(180deg, var(--color-msg-send-bg-start) 0%, var(--color-msg-send-bg-end));
  border-bottom-right-radius: 0;
}

.uploading-message--error {
  border-color: var(--color-danger, #dc3545);
}

.uploading-message__content {
  display: flex;
  flex-direction: column;
  gap: var(--spacing-xs);
}

.uploading-message__header {
  display: flex;
  justify-content: flex-end;
  align-items: center;
  gap: var(--spacing-xs);
}

.uploading-message__status {
  display: flex;
  align-items: center;
  gap: var(--spacing-xs);
  font-size: var(--font-size-xs);
  color: var(--color-text-label);
}

.uploading-message--error .uploading-message__status {
  color: var(--color-danger, #dc3545);
}

.uploading-message__spinner {
  display: inline-block;
  width: 12px;
  height: 12px;
  border: 2px solid var(--color-primary);
  border-right-color: transparent;
  border-radius: 50%;
  animation: spin 0.6s linear infinite;
}

.uploading-message__image-container {
  position: relative;
  overflow: hidden;
  border-radius: var(--border-radius);
}

.uploading-message__image {
  max-width: 100%;
  max-height: 200px;
  display: block;
  border-radius: var(--border-radius);
}

.uploading-message--uploading .uploading-message__image {
  opacity: 0.7;
}

@keyframes spin {
  to {
    transform: rotate(360deg);
  }
}
</style>
